import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Layout from './components/Layout/Layout';
import LoadingScreen from './components/LoadingScreen';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';

// Lazy load app pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Returns = lazy(() => import('./pages/Returns'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const Notices = lazy(() => import('./pages/Notices'));
const Settings = lazy(() => import('./pages/Settings'));
const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const MultiGstin = lazy(() => import('./pages/MultiGstin'));
const Profile = lazy(() => import('./pages/Profile'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Guide = lazy(() => import('./pages/Guide'));
const TcsReconciliation = lazy(() => import('./pages/TcsReconciliation'));
const BankReconciliation = lazy(() => import('./pages/BankReconciliation'));
const DataImport = lazy(() => import('./pages/DataImport'));
const Filing = lazy(() => import('./pages/Filing'));

// Lazy load marketing pages
const MarketingLayout = lazy(() => import('./components/Marketing/MarketingLayout'));
const Homepage = lazy(() => import('./pages/marketing/Homepage'));
const Pricing = lazy(() => import('./pages/marketing/Pricing'));
const Features = lazy(() => import('./pages/marketing/Features'));
const About = lazy(() => import('./pages/marketing/About'));
const Contact = lazy(() => import('./pages/marketing/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/marketing/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/marketing/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/marketing/RefundPolicy'));
const Security = lazy(() => import('./pages/marketing/Security'));
const GstinLookup = lazy(() => import('./pages/marketing/GstinLookup'));
const FeaturesComparison = lazy(() => import('./pages/marketing/FeaturesComparison'));
const Resources = lazy(() => import('./pages/marketing/Resources'));
const IntegrationsPage = lazy(() => import('./pages/marketing/IntegrationsPage'));
const GSTCalculator = lazy(() => import('./pages/marketing/tools/GSTCalculator'));
const HSNFinder = lazy(() => import('./pages/marketing/tools/HSNFinder'));
const LateFeeCalculator = lazy(() => import('./pages/marketing/tools/LateFeeCalculator'));
const InvoiceGenerator = lazy(() => import('./pages/marketing/tools/InvoiceGenerator'));
const GSTINValidator = lazy(() => import('./pages/marketing/tools/GSTINValidator'));
const DueDateTracker = lazy(() => import('./pages/marketing/tools/DueDateTracker'));
const ComplianceScore = lazy(() => import('./pages/marketing/tools/ComplianceScore'));
const EwayBillGenerator = lazy(() => import('./pages/marketing/tools/EwayBillGenerator'));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading } = useAuth();

    // DEV MODE: Bypass auth for local development
    const isDev = import.meta.env.DEV;
    if (isDev) {
        return <>{children}</>;
    }

    if (loading) {
        return <LoadingScreen />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/sign-in" replace />;
    }

    return <>{children}</>;
}

// Marketing page wrapper
function MarketingPage({ children }: { children: React.ReactNode }) {
    return (
        <MarketingLayout>
            {children}
        </MarketingLayout>
    );
}

// Tool pages use simple footer
function ToolPage({ children }: { children: React.ReactNode }) {
    return (
        <MarketingLayout simpleFooter>
            {children}
        </MarketingLayout>
    );
}

import { GoogleOAuthProvider } from '@react-oauth/google';

// ...

function App() {
    // Use environment variable with fallback for development
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '773569155032-6buq5jmb2m7fi858e1snpev0mgtns89i.apps.googleusercontent.com';
    
    // Log for debugging
    if (import.meta.env.DEV) {
        console.log('Google Client ID:', googleClientId ? 'Loaded' : 'Missing');
        console.log('VITE_GOOGLE_CLIENT_ID:', import.meta.env.VITE_GOOGLE_CLIENT_ID || 'Not set');
    }

    // Check if Google Client ID is configured
    if (!googleClientId || googleClientId === 'your-google-client-id') {
        console.error('Google OAuth is not configured. Please set VITE_GOOGLE_CLIENT_ID in your .env file.');
    }

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <GlobalErrorBoundary>
                <AuthProvider>
                    <Suspense fallback={<LoadingScreen />}>
                        <Routes>
                            {/* ... existing routes ... */}
                            {/* Public Marketing Routes */}
                            <Route
                                path="/"
                                element={
                                    <MarketingPage>
                                        <Homepage />
                                    </MarketingPage>
                                }
                            />
                            {/* ... */}
                            {/* Free Tools Routes */}
                            <Route path="/tools/gst-calculator" element={<ToolPage><GSTCalculator /></ToolPage>} />
                            <Route path="/tools/hsn-finder" element={<ToolPage><HSNFinder /></ToolPage>} />
                            <Route path="/tools/late-fee-calculator" element={<ToolPage><LateFeeCalculator /></ToolPage>} />
                            <Route path="/tools/invoice-generator" element={<ToolPage><InvoiceGenerator /></ToolPage>} />
                            <Route path="/tools/gstin-validator" element={<ToolPage><GSTINValidator /></ToolPage>} />
                            <Route path="/tools/due-date-tracker" element={<ToolPage><DueDateTracker /></ToolPage>} />
                            <Route path="/tools/compliance-score" element={<ToolPage><ComplianceScore /></ToolPage>} />
                            <Route path="/tools/eway-bill-generator" element={<ToolPage><EwayBillGenerator /></ToolPage>} />


                            <Route path="/sign-in/*" element={<SignIn />} />
                            <Route path="/sign-up/*" element={<SignUp />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/login" element={<Navigate to="/sign-in" replace />} />
                            <Route path="/register" element={<Navigate to="/sign-up" replace />} />

                            <Route
                                path="/onboarding"
                                element={
                                    <ProtectedRoute>
                                        <Onboarding />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Protected app routes */}
                            <Route
                                path="/app/*"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <Routes>
                                                <Route path="/" element={<Dashboard />} />
                                                <Route path="/dashboard" element={<Dashboard />} />
                                                <Route path="/filing" element={<Filing />} />
                                                <Route path="/reconcile" element={<Reconciliation />} />
                                                <Route path="/settings" element={<Settings />} />
                                                <Route path="/transactions" element={<Transactions />} />
                                                <Route path="/returns" element={<Returns />} />
                                                <Route path="/notices" element={<Notices />} />
                                                <Route path="/gstins" element={<MultiGstin />} />
                                                <Route path="/profile" element={<Profile />} />
                                                <Route path="/integrations" element={<Integrations />} />
                                                <Route path="/reconciliation" element={<Reconciliation />} />
                                                <Route path="/tcs-reconciliation" element={<TcsReconciliation />} />
                                                <Route path="/bank-reconciliation" element={<BankReconciliation />} />
                                                <Route path="/import" element={<DataImport />} />
                                                <Route path="*" element={<Navigate to="/app" replace />} />
                                            </Routes>
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Catch-all redirect */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                </AuthProvider>
            </GlobalErrorBoundary>
        </GoogleOAuthProvider>
    );
}

export default App;
// Test comment for index update
